import { API_URL, BASIC_REQUEST_HEADERS, HTTP } from '../../helpers/constants';
import request from '../../utils/request';
import {
  EEmailCheckResult,
  IChangeGiftCardDeliveryPayload,
  ICreateGiftCardPurchasePayload,
  ICreateGiftCardPurchaseResponse,
  IGiftCardBalance,
  IGiftCardOffer,
  IGiftCardPurchaseStatus,
  IPublicPromoCode,
  IReferral,
  IReferralInvite,
  ISetPromoCodesResponse,
} from './types';

const URL_GIFT_CARD_PURCHASES = `${API_URL}/gift-card-purchases`;

export class GiftCardsService {
  /**
   * @description Records a gift card purchase and returns the Payline token to pay it
   * @param {ICreateGiftCardPurchasePayload} payload
   * @returns {Promise<ICreateGiftCardPurchaseResponse>}
   */
  static async createPurchase(
    payload: ICreateGiftCardPurchasePayload,
  ): Promise<ICreateGiftCardPurchaseResponse> {
    return request(URL_GIFT_CARD_PURCHASES, {
      method: HTTP.METHODS.POST,
      headers: BASIC_REQUEST_HEADERS,
      body: JSON.stringify(payload),
    });
  }

  /**
   * @description Returns the payment status of a purchase, as the worker last read it from Payline
   * @param {string} purchaseId
   * @returns {Promise<IGiftCardPurchaseStatus>}
   */
  static async getPurchase(
    purchaseId: string,
  ): Promise<IGiftCardPurchaseStatus> {
    return request(`${URL_GIFT_CARD_PURCHASES}/${purchaseId}`, {
      method: HTTP.METHODS.GET,
      headers: BASIC_REQUEST_HEADERS,
    });
  }

  /**
   * @description Moves the date or the recipient of an e-card that has not left, with the token of the receipt link
   * @param {string} purchaseId
   * @param {IChangeGiftCardDeliveryPayload} payload
   * @returns {Promise<IGiftCardPurchaseStatus>}
   */
  static async changeDelivery(
    purchaseId: string,
    payload: IChangeGiftCardDeliveryPayload,
  ): Promise<IGiftCardPurchaseStatus> {
    return request(`${URL_GIFT_CARD_PURCHASES}/${purchaseId}/delivery`, {
      method: HTTP.METHODS.POST,
      headers: BASIC_REQUEST_HEADERS,
      body: JSON.stringify(payload),
    });
  }

  /**
   * @description The balance of a gift card. Rejects with a 404 for an unknown code
   * @param {string} code
   * @returns {Promise<IGiftCardBalance>}
   */
  static async getBalance(code: string): Promise<IGiftCardBalance> {
    return request(
      `${URL_GIFT_CARD_PURCHASES}/codes/${encodeURIComponent(code.trim())}`,
      { method: HTTP.METHODS.GET, headers: BASIC_REQUEST_HEADERS },
    );
  }

  /**
   * @description Checks the codes typed in the checkout and keeps the accepted ones on the cart
   * @param {string} cartId
   * @param {string[]} codes Every code typed, as typed
   * @param {string} [email] The email of the form, not yet saved on the cart
   * @returns {Promise<ISetPromoCodesResponse>}
   */
  static async setCartPromoCodes(
    cartId: string,
    codes: string[],
    email?: string,
  ): Promise<ISetPromoCodesResponse> {
    return request(`${API_URL}/booking/carts/${cartId}/promo-codes`, {
      method: HTTP.METHODS.PUT,
      headers: BASIC_REQUEST_HEADERS,
      body: JSON.stringify({ codes, email: email || undefined }),
    });
  }

  /**
   * @description Checks the 6 digits sent to the email of the cart, for a code kept for some emails
   * @param {string} cartId
   * @param {string} code
   * @param {string} [email] The email the 6 digits were sent to
   * @returns {Promise<{ result: EEmailCheckResult }>}
   */
  static async checkPromoEmail(
    cartId: string,
    code: string,
    email?: string,
  ): Promise<{ result: EEmailCheckResult }> {
    return request(
      `${API_URL}/booking/carts/${cartId}/promo-codes/email-check`,
      {
        method: HTTP.METHODS.POST,
        headers: BASIC_REQUEST_HEADERS,
        body: JSON.stringify({ code, email: email || undefined }),
      },
    );
  }

  /**
   * @description The code shown to every visitor in the banner of the site, if any
   * @returns {Promise<{ promoCode: IPublicPromoCode | null }>}
   */
  static async getPublicPromoCode(): Promise<{
    promoCode: IPublicPromoCode | null;
  }> {
    return request(`${API_URL}/promo-codes/public`, {
      method: HTTP.METHODS.GET,
      headers: BASIC_REQUEST_HEADERS,
    });
  }

  /**
   * @description The referral code and link of the customer of a paid order, made the first time
   * @param {string} orderId
   * @param {string} email The email of the order
   * @returns {Promise<IReferral>}
   */
  static async createReferral(
    orderId: string,
    email: string,
  ): Promise<IReferral> {
    return request(`${API_URL}/promo-codes/referrals`, {
      method: HTTP.METHODS.POST,
      headers: BASIC_REQUEST_HEADERS,
      body: JSON.stringify({ orderId, email }),
    });
  }

  /**
   * @description Who sends a referral link. Rejects with a 404 for an unknown code
   * @param {string} code
   * @returns {Promise<IReferralInvite>}
   */
  static async getReferral(code: string): Promise<IReferralInvite> {
    return request(
      `${API_URL}/promo-codes/referrals/${encodeURIComponent(code.trim())}`,
      { method: HTTP.METHODS.GET, headers: BASIC_REQUEST_HEADERS },
    );
  }

  /**
   * @description The travellers offer of an order email. Rejects with a 404 once used or over
   * @param {string} token
   * @returns {Promise<IGiftCardOffer>}
   */
  static async getGiftOffer(token: string): Promise<IGiftCardOffer> {
    return request(
      `${URL_GIFT_CARD_PURCHASES}/offers/${encodeURIComponent(token)}`,
      { method: HTTP.METHODS.GET, headers: BASIC_REQUEST_HEADERS },
    );
  }
}
